import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  FreshnessBadge,
  PageHeader,
  PhotoTile,
  Skeleton,
  SourceBadge,
  Table,
  TableWrap,
  TD,
  TH,
  THead,
  TR,
  VerdictBadge,
} from "@/components/ui";
import type { components } from "@/generated/openapi-types";
import { useApi } from "@/lib/api";
import { cn } from "@/lib/cn";
import { ago, count, date, decimal, percent, personLabel } from "@/lib/format";

type Fact = components["schemas"]["AdminCafeFact"];

/**
 * One café, and everything the trust model knows about it.
 *
 * The facts table is the reason this screen exists. A verdict on its own is
 * an assertion the console makes; an operator resolving a dispute needs to
 * see who claimed what, on what evidence, and when — including the claims
 * that have since been superseded, because "it used to say something else"
 * is usually what the dispute turns on. Expanding a fact shows exactly that.
 */
export function CafeDetailScreen() {
  const { cafeId = "" } = useParams();
  const api = useApi();
  const queryClient = useQueryClient();

  const cafe = useQuery({
    queryKey: ["admin", "cafe", cafeId],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/cafes/{cafeId}", {
        params: { path: { cafeId } },
      });
      if (error) throw error;
      return data;
    },
  });

  const setStatus = useMutation({
    mutationFn: async (status: "open" | "closed") => {
      const { error } = await api.PATCH("/admin/cafes/{cafeId}", {
        params: { path: { cafeId } },
        body: { status },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "cafe", cafeId],
      });
      void queryClient.invalidateQueries({ queryKey: ["admin", "cafes"] });
    },
  });

  if (cafe.isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader eyebrow="Corpus" title="Cafe" />
        <ErrorState onRetry={() => void cafe.refetch()} />
      </div>
    );
  }

  const d = cafe.data;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="Corpus"
        title={d?.cafe.name ?? "…"}
        description={
          d
            ? [d.cafe.street, d.housenumber, d.postcode, d.cafe.city]
                .filter(Boolean)
                .join(" ")
            : undefined
        }
        actions={
          d ? (
            <>
              <Link
                to={`/reviews?cafe=${cafeId}`}
                className="text-[12px] text-accent hover:underline"
              >
                All reviews
              </Link>
              {d.cafe.status === "open" ? (
                <Button
                  variant="danger"
                  disabled={setStatus.isPending}
                  onClick={() => setStatus.mutate("closed")}
                >
                  Mark closed
                </Button>
              ) : (
                <Button
                  variant="primary"
                  disabled={setStatus.isPending}
                  onClick={() => setStatus.mutate("open")}
                >
                  Reopen
                </Button>
              )}
            </>
          ) : null
        }
      />

      {!d ? (
        <Skeleton className="h-96" />
      ) : (
        <>
          <section className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <Card>
              <CardHeader
                title="What we say about it"
                subtitle="Every claim behind every fact, newest first. Superseded claims are kept and marked."
              />
              {d.facts.length === 0 ? (
                <p className="py-6 text-center text-micro text-muted">
                  Nothing has been claimed about this cafe yet. It came from the
                  import and nobody has been.
                </p>
              ) : (
                <div className="flex flex-col divide-y divide-border-2">
                  {d.facts.map((fact) => (
                    <FactBlock key={fact.key} fact={fact} />
                  ))}
                </div>
              )}
            </Card>

            <div className="flex flex-col gap-3">
              <Card>
                <CardHeader title="Rating" />
                {d.score ? (
                  <dl className="grid grid-cols-2 gap-3">
                    <Stat
                      label="Matcha"
                      value={decimal(d.score.matchaValue)}
                      hint={`${count(d.score.matchaCount)} ratings · ${percent(
                        d.score.matchaConfidence * 100,
                      )} confidence`}
                    />
                    <Stat
                      label="Room"
                      value={decimal(d.score.roomValue)}
                      hint={`${count(d.score.roomCount)} ratings`}
                    />
                    <Stat
                      label="Computed"
                      value={ago(d.score.computedAt)}
                      hint="by the rollup"
                    />
                    <Stat label="Reviews" value={count(d.cafe.reviewCount)} />
                  </dl>
                ) : (
                  <p className="text-micro text-muted">
                    No score yet — a cafe needs ratings before the rollup gives
                    it one.
                  </p>
                )}
              </Card>

              <Card>
                <CardHeader
                  title="Where it came from"
                  subtitle="Provenance, what we think, and whether anybody has confirmed it."
                />
                <dl className="flex flex-col gap-2">
                  <Row label="Source" value={d.cafe.source} />
                  <Row
                    label="Plausibility"
                    value={
                      <span className="flex items-center gap-1.5">
                        <Badge
                          tone={
                            d.cafe.prior === "likely"
                              ? "accent"
                              : d.cafe.prior === "skip"
                                ? "neutral"
                                : "warn"
                          }
                        >
                          {d.cafe.prior}
                        </Badge>
                        <span className="font-mono text-[11px] text-muted">
                          score {d.priorScore}
                        </span>
                      </span>
                    }
                  />
                  {d.priorReasons.length > 0 ? (
                    <Row
                      label="Because"
                      value={
                        <span className="text-micro text-muted">
                          {d.priorReasons.join(", ")}
                        </span>
                      }
                    />
                  ) : null}
                  <Row
                    label="OSM"
                    value={
                      d.osmId
                        ? `${d.osmType ?? "?"} ${d.osmId}`
                        : "not from OSM"
                    }
                  />
                  <Row label="First seen" value={date(d.firstSeenAt)} />
                  <Row label="Last seen" value={date(d.lastSeenAt)} />
                  <Row
                    label="Coordinates"
                    value={`${d.lat.toFixed(5)}, ${d.lon.toFixed(5)}`}
                  />
                  {d.website ? (
                    <Row
                      label="Website"
                      value={
                        <a
                          href={d.website}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="text-accent hover:underline"
                        >
                          {d.website.replace(/^https?:\/\//, "")}
                        </a>
                      }
                    />
                  ) : null}
                  {d.openingHours ? (
                    <Row label="Hours" value={d.openingHours} />
                  ) : null}
                </dl>
              </Card>
            </div>
          </section>

          {d.photos.length > 0 ? (
            <Card>
              <CardHeader
                title="Photos"
                subtitle={`${count(d.photos.length)} from reviews here`}
              />
              <div className="flex flex-wrap gap-2">
                {d.photos.map((photo) => (
                  <PhotoTile key={photo.id} photo={photo} />
                ))}
              </div>
            </Card>
          ) : null}

          <Card padded={false}>
            <div className="px-4 pt-4">
              <CardHeader
                title="Recent reviews"
                subtitle="Newest first. Open one to see its photos and any reports."
              />
            </div>
            {d.recentReviews.length === 0 ? (
              <p className="px-4 pb-6 text-micro text-muted">
                Nobody has reviewed this cafe.
              </p>
            ) : (
              <TableWrap>
                <Table>
                  <THead>
                    <TR>
                      <TH>Note</TH>
                      <TH>Person</TH>
                      <TH align="right">Matcha</TH>
                      <TH align="right">Photos</TH>
                      <TH>Posted</TH>
                    </TR>
                  </THead>
                  <tbody>
                    {d.recentReviews.map((review) => (
                      <TR key={review.id}>
                        <TD className="max-w-[420px]">
                          <Link
                            to={`/reviews/${review.id}`}
                            className="block truncate text-ink-2 hover:text-accent hover:underline"
                          >
                            {review.note ?? "no note"}
                          </Link>
                        </TD>
                        <TD>{personLabel(review.user)}</TD>
                        <TD align="right" mono>
                          {decimal(review.rating)}
                        </TD>
                        <TD align="right" mono>
                          {count(review.photoCount)}
                        </TD>
                        <TD muted>{ago(review.createdAt)}</TD>
                      </TR>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function FactBlock({ fact }: { fact: Fact }) {
  return (
    <details className="group py-2.5 first:pt-0 last:pb-0">
      <summary className="flex cursor-pointer list-none items-center gap-2">
        <span className="w-[132px] shrink-0 text-[12.5px] text-ink-2">
          {fact.label}
        </span>
        <span className="font-mono text-[12px] text-ink">
          {fact.value ?? "—"}
        </span>
        <VerdictBadge verdict={fact.verdict} />
        <FreshnessBadge freshness={fact.freshness} />
        <span className="ml-auto flex items-center gap-2 font-mono text-[11px] text-muted">
          <span title="people who said so">{fact.humanCount} ppl</span>
          <span title="machine claims">{fact.machineCount} bot</span>
          <span className="text-muted-2 group-open:hidden">
            {fact.assertions.length} claims ▸
          </span>
          <span className="hidden text-muted-2 group-open:inline">▾</span>
        </span>
      </summary>
      {fact.assertions.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-1.5 border-l border-border-2 pl-3">
          {fact.assertions.map((assertion) => (
            <li
              key={assertion.id}
              className={cn(
                "flex flex-wrap items-center gap-2 text-[11.5px]",
                assertion.superseded && "opacity-55",
              )}
            >
              <SourceBadge source={assertion.source} />
              <span className="font-mono text-ink-2">{assertion.value}</span>
              <span className="text-muted">
                {assertion.user ? personLabel(assertion.user) : "no person"}
              </span>
              <span className="font-mono text-muted-2">
                w{assertion.weight}
              </span>
              <span className="text-muted-2">{ago(assertion.observedAt)}</span>
              {assertion.superseded ? (
                <Badge tone="neutral">superseded</Badge>
              ) : null}
              {assertion.evidenceQuote ? (
                <span className="w-full pl-1 text-micro italic text-muted">
                  “{assertion.evidenceQuote}”
                  {assertion.evidenceUrl ? (
                    <a
                      href={assertion.evidenceUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="ml-1 not-italic text-accent hover:underline"
                    >
                      source
                    </a>
                  ) : null}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 pl-3 text-micro text-muted">
          The rollup has a state row but no surviving claims.
        </p>
      )}
    </details>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div>
      <dt className="eyebrow">{label}</dt>
      <dd className="mt-0.5 font-mono text-[16px] leading-none text-ink">
        {value}
      </dd>
      {hint ? <dd className="mt-1 text-micro text-muted">{hint}</dd> : null}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="w-[92px] shrink-0 text-micro text-muted">{label}</dt>
      <dd className="min-w-0 text-[12px] text-ink-2">{value}</dd>
    </div>
  );
}
